<script setup lang="ts">
import { computed } from "vue"
import MediaEditForm from "../media/MediaEditForm.vue"
import CopyFromPopover from "./CopyFromPopover.vue"
import { useUploadEditSessionContext } from "./UploadEditSessionKey"
import { createEmptyMediaFormData, type MediaFormData } from "../media/media"
import { notifyError } from "../notifications"
import FormShell from "../forms/components/FormShell.vue"
import { useForm } from "../forms/composables/useForm"

interface Candidate {
  id: string
  filename: string
  draft: MediaFormData
}

defineProps<{
  previewUrl?: string
  otherCandidates: Candidate[]
}>()

const session = useUploadEditSessionContext()

const activeId = computed(() => session.activeId.value)
const draft = computed<MediaFormData | undefined>(() =>
  activeId.value ? session.formDrafts.value.get(activeId.value) : undefined
)

// Local form context for the form system (FormField/FormSection require a
// FormShell ancestor via useFormContext). The session is still the source of
// truth; this form mirrors the active draft so validators in <FormField> see
// the current value. On active-id switch, useForm's internal watch on
// `initial` reseeds `form.value` via deep-clone.
const initial = computed<MediaFormData>(() =>
  draft.value ?? createEmptyMediaFormData()
)
const form = useForm<MediaFormData>({
  initial,
  save: async () => {
    if (!activeId.value) return
    await session.saveSingle(activeId.value)
  }
})

function onUpdate(next: MediaFormData): void {
  if (!activeId.value) return
  form.value.value = next
  session.patchDraft(activeId.value, next)
}

async function onSave(): Promise<void> {
  if (!activeId.value) return
  try {
    await session.saveSingle(activeId.value)
  } catch (err) {
    notifyError({ description: `Datei konnte nicht gespeichert werden: ${err instanceof Error ? err.message : String(err)}` })
  }
}

function onCopyFrom(sourceId: string): void {
  if (!activeId.value) return
  session.applyFromSource(activeId.value, sourceId)
}
</script>

<template>
  <div v-if="draft" class="flex flex-col h-full min-w-0">
    <div class="p-4 border-b flex items-start gap-4 min-w-0">
      <img
        v-if="previewUrl"
        data-testid="single-preview"
        :src="previewUrl"
        alt="Vorschau"
        class="w-32 h-32 object-contain bg-gray-100 rounded shrink-0"
      >
      <div class="flex-1 min-w-0 flex flex-col gap-3 self-stretch">
        <div class="font-medium truncate" :title="draft.filename">{{ draft.filename }}</div>
        <div class="mt-auto flex justify-end">
          <CopyFromPopover
            :candidates="otherCandidates"
            @select="onCopyFrom"
          />
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto p-4">
      <FormShell :form="form">
        <MediaEditForm
          :model-value="draft"
          mode="single"
          @update:model-value="onUpdate"
        />
      </FormShell>
    </div>

    <div class="p-4 border-t flex justify-end">
      <button
        data-testid="single-save"
        type="button"
        class="btn btn-primary"
        @click="onSave"
      >
        Speichern
      </button>
    </div>
  </div>
</template>
